/**
 * DeleteDocumentDatabaseModal Component
 *
 * Modal for confirming document database deletion.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteDocumentDatabase } from '../hooks/useDeleteDocumentDatabase';
import type { DocumentDatabaseSanitized } from '../../../types/document-database.types';

interface DeleteDocumentDatabaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  documentDB: DocumentDatabaseSanitized;
}

/**
 * DeleteDocumentDatabaseModal component
 */
export const DeleteDocumentDatabaseModal: React.FC<DeleteDocumentDatabaseModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  documentDB,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutate: deleteDocumentDatabase, isPending } = useDeleteDocumentDatabase(tenantId, {
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      setErrorMessage(error);
    },
  });

  const handleDelete = () => {
    setErrorMessage('');
    deleteDocumentDatabase(documentDB.documentDBId);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      onClose();
    }
  };

  const hasAccessGrants = documentDB.accessGrants && documentDB.accessGrants.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Delete Document Database</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Warning for access grants */}
          {hasAccessGrants && (
            <Alert variant="warning">
              This document database has {documentDB.accessGrants?.length} application(s) with access.
              You must revoke all access grants before deleting.
            </Alert>
          )}

          {/* Confirmation Message */}
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the document database{' '}
            <span className="font-medium text-gray-900">{documentDB.documentDBName}</span>
            <span className="ml-1 font-mono text-xs text-gray-500">
              ({documentDB.documentDBCode})
            </span>
            ?
          </p>

          <p className="text-sm text-red-600 font-medium">
            This action cannot be undone. All documents stored in this database will be permanently deleted.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={isPending}
            disabled={hasAccessGrants}
          >
            Delete Database
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
